import { RefObject, useCallback, useState } from 'react';

import { useHeroToast } from '../../../hooks';
import { usePostAdCreative, usePutAdCreative } from '../../../queries';
import { AdStageHandle } from '../components/Stage/AdStage';
import { SavedKind, useAdsGeneratorStore } from '../model/store';
import { uploadToBytescale } from '../utils/bytescale';
import { toSavedScene } from '../utils/scene';

/** Dłuższy bok miniatury zapisywanej razem z kreacją. */
const PREVIEW_MAX_SIZE = 480;

interface SaveArgs {
  name: string;
  kind: SavedKind;
  /** Wymusza nowy wpis, nawet gdy plansza pochodzi z zapisanej kreacji. */
  asNew?: boolean;
}

interface SaveCreativeApi {
  save: (args: SaveArgs) => Promise<boolean>;
  isSaving: boolean;
}

/**
 * Zapis planszy do biblioteki: miniatura idzie do Bytescale, a scena (bez
 * obrazków, z samymi adresami) do Firestore'a.
 *
 * Nieudany upload miniatury nie blokuje zapisu — kreacja bez podglądu jest
 * dużo mniejszym problemem niż utrata pracy nad planszą.
 */
export const useSaveCreative = (stageRef: RefObject<AdStageHandle | null>): SaveCreativeApi => {
  const scene = useAdsGeneratorStore((state) => state.scene);
  const saved = useAdsGeneratorStore((state) => state.saved);
  const markSaved = useAdsGeneratorStore((state) => state.markSaved);
  const { showError, showSuccess } = useHeroToast();
  const { mutateAsync: create } = usePostAdCreative();
  const { mutateAsync: update } = usePutAdCreative();
  const [isSaving, setIsSaving] = useState(false);

  const capturePreview = useCallback(async (name: string): Promise<string> => {
    try {
      const blob = await stageRef.current?.capture(PREVIEW_MAX_SIZE);
      if (!blob) return '';
      const uploaded = await uploadToBytescale(blob, `${name || 'kreacja'}-podglad.jpg`);
      return uploaded.url;
    } catch {
      return '';
    }
  }, [stageRef]);

  const save = useCallback(async ({ name, kind, asNew }: SaveArgs): Promise<boolean> => {
    if (!scene) return false;

    setIsSaving(true);
    try {
      const preview = await capturePreview(name);
      const payload = {
        name,
        kind,
        platformId: scene.platformId,
        formatId: scene.formatId,
        width: scene.width,
        height: scene.height,
        scene: toSavedScene(scene),
        ...(preview ? { preview } : {}),
      };

      const canUpdate = !asNew && saved !== null && saved.kind === kind;

      if (canUpdate) {
        await update({ id: saved.id, data: payload });
        markSaved({ id: saved.id, name, kind });
        showSuccess(kind === 'template' ? 'Szablon zaktualizowany' : 'Kreacja zaktualizowana');
      } else {
        const created = await create({ data: payload });
        markSaved({ id: created.id, name, kind });
        showSuccess(kind === 'template' ? 'Szablon zapisany w bazie' : 'Kreacja zapisana w bibliotece');
      }

      return true;
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Nie udało się zapisać kreacji');
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [capturePreview, create, markSaved, saved, scene, showError, showSuccess, update]);

  return { save, isSaving };
};
